import { useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { QUERY_PROGRESS_PENDING } from "core/consts";
import { generateTrackName } from "core/validationSchemas/utils";
import Tooltip from "ds_legacy/components/Tooltip";
import { GREY_200, GREY_600 } from "ds_legacy/materials/colors";
import {
  border,
  dp,
  important,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import {
  SHADOW_BUTTON,
  SHADOW_BUTTON_HOVER,
} from "ds_legacy/materials/shadows";
import {
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_20,
} from "ds_legacy/materials/typography";
import React, { forwardRef, useCallback, useRef } from "react";
import { useTracking } from "react-tracking";
import styled, { keyframes } from "styled-components";
import { ColorData, backgroundColors, setInvertion } from "./colors";
import {
  RSButtonColor,
  RSButtonIcon,
  RSButtonProps,
  RSButtonSize,
  RSButtonVariant,
} from "./index";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div<{
  color: "primary" | "secondary";
  parentHeight: number | undefined;
}>`
  border: ${(props) =>
    border({
      color: (props.theme as any).palette[props.color].light,
      width: 4.5,
    })}; /* background */
  border-top: ${(props) =>
    border({
      color: (props.theme as any).palette[props.color].dark,
      width: 4.5,
    })}; /* spinner */
  border-radius: 50%;
  width: ${(props) => dp(props.parentHeight ? props.parentHeight / 2.25 : 16)};
  height: ${(props) => dp(props.parentHeight ? props.parentHeight / 2.25 : 16)};
  animation: ${spin} 1s linear infinite;
`;

const IconWrap = styled.span<{ right: boolean }>`
  font-size: ${FONT_SIZE_20};
  display: flex;
  ${(props) =>
    props.right ? `margin-left: ${sizing(1)}` : `margin-right: ${sizing(1)}`};
`;

function getMaterialUiColor(color: RSButtonColor | "inherit" | undefined) {
  switch (color) {
    case "default":
      return "primary";
    case "inherit":
    case "primary":
    case "secondary":
      return color;
    default:
      return undefined;
  }
}

function getIcon(
  IconComponent: RSButtonIcon | undefined,
  right: boolean,
  variant: RSButtonVariant,
  size: RSButtonSize,
  inverted?: boolean,
) {
  if (!IconComponent) return null;

  const shouldGetOpacity =
    (variant === "text" && !inverted) ||
    (variant === "contained" && inverted) ||
    (variant === "outlined" && !inverted);

  return (
    <IconWrap right={right}>
      <IconComponent
        fontSize="inherit"
        size={size === "small" ? FONT_SIZE_14 : FONT_SIZE_16}
        style={shouldGetOpacity ? { opacity: 0.6 } : null}
      />
    </IconWrap>
  );
}

function Loading({
  children,
  color,
  isLoading,
}: {
  children: React.ReactNode;
  color?: RSButtonColor;
  isLoading?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return (
      <>
        <div style={{ display: "flex", visibility: "hidden" }}>{children}</div>
        <div
          ref={ref}
          style={{
            alignItems: "center",
            display: "flex",
            flex: 1,
            height: "100%",
            justifyContent: "center",
            position: "absolute",
            width: "100%",
          }}
        >
          <Spinner
            color={color == "secondary" ? "secondary" : "primary"}
            parentHeight={ref.current?.clientHeight}
          />
        </div>
      </>
    );
  }

  return <>{children}</>;
}

export const hoverColorSelector = (
  color: RSButtonColor,
  inverted: boolean | undefined,
  variant?: RSButtonVariant,
): RSButtonColor => {
  if (inverted) {
    if (variant === "text") {
      return "white";
    }
    return `${color}-dark` as RSButtonColor;
  }

  if (color === "secondary") {
    return "default";
  }
  if (variant === "text") {
    return `${color}-dark` as RSButtonColor;
  }
  return "white";
};

export const hoverBackgroundColorSelector = (
  color: RSButtonColor,
  inverted: boolean | undefined,
  variant?: RSButtonVariant,
): RSButtonColor => {
  if (inverted) {
    if (variant === "text") {
      return `${color}-dark` as RSButtonColor;
    }
    return "white";
  }

  if (variant === "contained") {
    return `${color}-dark` as RSButtonColor;
  }
  if (variant === "text") {
    return "white";
  }
  return color;
};

const RSButton = forwardRef<HTMLButtonElement, RSButtonProps>(
  (
    {
      ariaProps,
      buttonWrapperStyle,
      children,
      className,
      color = "default",
      dataTestId,
      disabled,
      id = "rs_button",
      inverted,
      LeftIcon,
      loading,
      onClick,
      RightIcon,
      size = "medium",
      style,
      tabIndex,
      tooltip,
      tour,
      trackEventProps,
      type,
      variant,
    },
    ref,
  ) => {
    const { trackEvent } = useTracking();
    const theme = useTheme();
    const borderColor = ["primary", "secondary", "success", "error"].includes(
      color,
    );

    const getSx = useCallback(() => {
      {
        // Base color setup:
        let colorData: ColorData = {
          backgroundColor: backgroundColors(theme)[color],
          borderColor: borderColor
            ? backgroundColors(theme)[color]
            : theme.palette.common.black,
          boxShadow: variant === "contained" ? SHADOW_BUTTON : "",
          color:
            color === "secondary"
              ? theme.palette.common.black
              : theme.palette.common.white,
        };

        // setup invertion
        colorData = setInvertion(colorData, variant, inverted);

        colorData = {
          ...colorData,
          "&:hover": {
            backgroundColor:
              backgroundColors(theme)[
                hoverBackgroundColorSelector(color, inverted, variant)
              ],
            borderColor: borderColor
              ? backgroundColors(theme)[color]
              : theme.palette.common.black,
            boxShadow: variant === "contained" ? SHADOW_BUTTON_HOVER : "",
            color:
              backgroundColors(theme)[
                hoverColorSelector(color, inverted, variant)
              ],
            outline:
              variant === "text" && !inverted
                ? `${dp(1)} solid ${backgroundColors(theme)[color]}`
                : undefined,
          },
        };

        // in case of text remove background
        if (variant === "text" && !inverted)
          colorData = {
            ...colorData,
            backgroundColor: "transparent",
          };

        if (disabled) {
          colorData = {
            ...colorData,
            backgroundColor: important(GREY_200),
            borderWidth: important("0"),
            color: important(GREY_600),
          };
        }

        return {
          ...colorData,
          borderRadius: variant !== "contained" ? dp(2) : dp(4),
          cursor: "pointer",
          margin: margin(1),
          minHeight: sizing(4.5),
          minWidth: sizing(8),
          // required for accessiblity to ensure focus outline is visible
          outlineOffset: dp(1),
          pointerEvents: loading === QUERY_PROGRESS_PENDING ? "none" : "auto",
          position: "relative",
          width: "fit-content",
          ...style,
        };
      }
    }, [
      color,
      disabled,
      inverted,
      loading,
      theme,
      variant,
      ...Object.values(style ?? {}),
    ]);

    return (
      <Tooltip title={tooltip}>
        <div
          data-testid="rs-button-tooltip"
          style={{
            alignItems: "center",
            boxSizing: "border-box",
            display: "flex",
            // 1px additional padding for the outline offset
            padding: padding(1 / 8),
            ...buttonWrapperStyle,
          }}
        >
          <Button
            {...ariaProps}
            {...tour}
            ref={ref}
            className={className}
            color={getMaterialUiColor(color)}
            data-testid={dataTestId ?? id}
            disabled={disabled || loading === QUERY_PROGRESS_PENDING}
            id={id}
            size={size}
            sx={getSx()}
            tabIndex={tabIndex}
            type={type ?? "button"}
            variant={variant}
            onClick={(e) => {
              const otherTrackProps = { ...(trackEventProps ?? {}) };
              delete otherTrackProps.type;
              trackEvent({
                name: generateTrackName({
                  id,
                  trackEventProps: trackEventProps,
                  type: trackEventProps?.type,
                }),
                ...otherTrackProps,
              });
              onClick(e);
            }}
          >
            <Loading
              color={color}
              isLoading={loading === QUERY_PROGRESS_PENDING}
            >
              {getIcon(LeftIcon, false, variant, size, inverted)}
              {children}
              {getIcon(RightIcon, true, variant, size, inverted)}
            </Loading>
          </Button>
        </div>
      </Tooltip>
    );
  },
);

export default RSButton;
